import React, { useMemo } from "react";
import { ClipboardCheck, FileText } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useParams } from "react-router-dom";
import SurveyRow from "@components/SurveyPickerCst/components/SurveyRow";
import { SurveyListItem } from "@components/SurveyPickerCst/types";
import AddItemsModalCst, {
  AddItemsConfirmEntry,
  AddItemsTabConfig,
} from "@components/AddItemsModalCst";
import { usePaginatedList } from "@hooks/usePaginatedList";
import { ServicesURL } from "@utils/utilsApi";

export type AddTestSurveyKind = "test" | "survey";

export interface AddTestSurveySelectedItem {
  id: number;
  label: string;
  kind: AddTestSurveyKind;
}

interface AddTestSurveyModalCstProps {
  open: boolean;
  onClose: () => void;
  onConfirm: (selection: AddTestSurveySelectedItem | null) => void;
  initialTab?: AddTestSurveyKind;
  visibleTabs?: AddTestSurveyKind[];
  onCreate?: (tabKey: AddTestSurveyKind) => void;
}

const AddTestSurveyModalCst: React.FC<AddTestSurveyModalCstProps> = ({
  open,
  onClose,
  onConfirm,
  initialTab = "test",
  visibleTabs = ["test", "survey"],
  onCreate,
}) => {
  const { t } = useTranslation("add-items-modal");
  const { id: corporateId = "" } = useParams();

  const testUrl = corporateId
    ? ServicesURL.getActivities.replace(":idCorporate", corporateId)
    : null;
  const surveyUrl = corporateId
    ? `${ServicesURL.getSurvey}?corporateId=${corporateId}`
    : null;

  const useTestData = (args: {
    page: number;
    pageSize: number;
    search?: string;
  }) => {
    const result = usePaginatedList<Record<string, any>>(testUrl, {
      page: args.page,
      pageSize: args.pageSize,
      search: args.search,
      fieldFilters: { type: "DA_XAPI,DA_SCORM", is_standalone: "false" },
    });
    const normalized: SurveyListItem[] = useMemo(
      () =>
        result.items.map((it) => ({
          id: Number(it.id),
          name: (it.title || it.name || "") as string,
        })),
      [result.items],
    );
    return { ...result, items: normalized };
  };

  const useSurveyData = (args: {
    page: number;
    pageSize: number;
    search?: string;
  }) =>
    usePaginatedList<SurveyListItem>(
      surveyUrl,
      {
        page: args.page,
        pageSize: args.pageSize,
        search: args.search,
      },
      {
        responseKey: "content",
        totalElementsKey: "totalElements",
        totalPagesKey: "totalPages",
      },
    );

  const renderRow = ({ item }: { item: SurveyListItem }) => (
    <SurveyRow item={item} />
  );

  const allTabs: AddItemsTabConfig<SurveyListItem>[] = [
    {
      key: "test",
      label: String(t("tabs.tests") ?? "Test"),
      icon: <ClipboardCheck size={14} />,
      useDataHook: useTestData,
      renderRow,
      getId: (it) => it.id,
    },
    {
      key: "survey",
      label: String(t("tabs.surveys") ?? "Survey"),
      icon: <FileText size={14} />,
      useDataHook: useSurveyData,
      renderRow,
      getId: (it) => it.id,
    },
  ];

  const tabs = allTabs.filter((tabCfg) =>
    visibleTabs.includes(tabCfg.key as AddTestSurveyKind),
  );

  const handleConfirm = (entries: AddItemsConfirmEntry<SurveyListItem>[]) => {
    if (entries.length === 0) {
      onConfirm(null);
    } else {
      const e = entries[0];
      onConfirm({
        id: e.item.id,
        label: e.item.name,
        kind: e.tabKey as AddTestSurveyKind,
      });
    }
    onClose();
  };

  return (
    <AddItemsModalCst<SurveyListItem>
      open={open}
      onClose={onClose}
      title={String(t("title.add-test-survey") ?? "Aggiungi Test e Survey")}
      selectionMode="single"
      tabs={tabs}
      initialTab={initialTab}
      onConfirm={handleConfirm}
      onCreate={
        onCreate ? (tab) => onCreate(tab as AddTestSurveyKind) : undefined
      }
      confirmLabel={String(t("confirm") ?? "Aggiungi")}
      createLabel={String(t("create") ?? "Crea")}
    />
  );
};

export default AddTestSurveyModalCst;
